import { Box, Paper } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import WarehouseFormDialog from "../components/WarehouseEdit/WarehouseFormDialog"
import WarehouseFilterChips from "../components/WarehousesTable/WarehouseFilterChips"
import { WarehouseFilterDrawer } from "../components/WarehousesTable/WarehouseFilterDrawer"
import { WarehouseSearch } from "../components/WarehousesTable/WarehouseSearch"
import WarehousesTable from "../components/WarehousesTable/WarehousesTable"

import { PageHeader } from "@/components/PageHeader/PageHeader"
import { SortingOrder, type WarehouseAreaSortField } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { useWarehouseColumns } from "@/screens/Warehouses/hooks"
import { useWarehousesSearchStore } from "@/screens/Warehouses/stores"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

const WarehousesContainer: FC = () => {
  const navigate = useNavigate()
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const currentSearch = useSearch({ from: "/app/admin/warehouses/" })
  const { query, field: sortField, order, ...restSearchParams } = currentSearch

  const data = useWarehousesSearchStore(({ data }) => data)
  const fetching = useWarehousesSearchStore(({ fetching }) => fetching)
  const error = useWarehousesSearchStore(({ error }) => error)
  const executeSearch = useWarehousesSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useWarehousesSearchStore(({ loadMore }) => loadMore)
  const clearStore = useWarehousesSearchStore(({ clearStore }) => clearStore)

  const refreshWarehouses = () => {
    executeSearch(queryClient, currentSearch, true)
  }

  const columns = useWarehouseColumns(refreshWarehouses)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    navigate({
      search: {
        isActive: true,
      },
      to: "/app/admin/warehouses",
      replace: true,
    })
  }, [navigate])

  useEffect(() => {
    executeSearch(queryClient, currentSearch)
  }, [executeSearch, currentSearch])

  const sorting = useMemo(() => {
    if (!sortField) return []

    const tableSortField = upperSnakeCaseToCamelCase(sortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [sortField, order])

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id
      const sortedField = toUpperSnakeCase(key) as WarehouseAreaSortField
      const params = {
        ...restSearchParams,
        query,
        field: sortedField,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }
      const refinedParams = getRefinedSearchParams(params)
      const { field, order, query: newQuery } = refinedParams.search
      navigate({
        search: { field, order, query: newQuery },
        to: "/app/admin/warehouses",
        replace: true,
      })
    }
  }

  const [throttledLoadMore] = useThrottle(() => {
    loadMore(queryClient, currentSearch)
  }, 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const handleFilterDrawerOpen = () => setIsFilterDrawerOpen(true)

  const handleFilterDrawerToggle = useCallback(() => setIsFilterDrawerOpen((prev) => !prev), [])

  const handleCreateDialogOpen = () => setIsCreateDialogOpen(true)

  const handleCreateDialogSubmit = () => {
    setIsCreateDialogOpen(false)
    refreshWarehouses()
  }

  const handleCreateDialogClose = () => setIsCreateDialogOpen(false)

  return (
    <>
      <Paper
        elevation={0}
        className='flex min-h-0 grow flex-col overflow-hidden rounded-lg bg-white'
        data-testid='warehouses'
      >
        <PageHeader title='Manage Warehouses' />
        <WarehouseSearch onFilterOpen={handleFilterDrawerOpen} onCreateDialogOpen={handleCreateDialogOpen} />
        <WarehouseFilterDrawer showFilter={isFilterDrawerOpen} toggleFilter={handleFilterDrawerToggle} />
        <WarehouseFilterChips />
        <Box className='flex flex-1 flex-col overflow-y-auto' data-test='warehouses-table-container'>
          <WarehousesTable
            data={data}
            fetching={fetching}
            columns={columns}
            sorting={sorting}
            error={error}
            tableContainerRef={tableContainerRef}
            onSortingChange={handleSortingChange}
            onScroll={handleScroll}
          />
        </Box>
      </Paper>
      {isCreateDialogOpen && (
        <WarehouseFormDialog warehouse={null} onSubmit={handleCreateDialogSubmit} onClose={handleCreateDialogClose} />
      )}
    </>
  )
}

export default WarehousesContainer
